/* eslint-disable react/jsx-no-useless-fragment */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";

// Redux
import { useAppDispatch, useAppSelector } from "../store";
import { selectAsset } from "../pages/navBarSlice";
import {
  assetIdNav,
  assetTypeNav,
  assetUrlNav,
} from "../pages/assetSliceNavBar";
import { RootState } from "../rootReducer";
import { setAssets } from "../pages/assetsSlice";
import { initialState, setInPageNavState } from "./inPageNavSlice";

// Interfaces
import {
  AssetInfo,
  DocumentThumbnail,
  VideoThumbnail,
} from "../models/interfaces/common";

// Utils
import {
  getDocumentThumbnails,
  getAssetThumbnailUrl,
  getVideoThumbnails,
  checkIfHasNextCatalogs,
  checkIfHasNextSearchAndViewAll,
} from "../utils/catalog";

// Components
import CatalogCard from "./CatalogCard";
import MobileSelectionControls from "./MobileSelectionControls";

// Custom Hooks
import useMultiSelection from "../hooks/useMultiSelection";
import { setPageNumber } from "@/pages/sortSlice";
import SmallLoader from "./loaders/SmallLoader";

interface CatalogCardsProps {
  assetLength?: number;
  loading: boolean;
  restApiAssets: AssetInfo[] | null;
  pageNumberProp?: number;
  hasNext: boolean;
  setHasNext: (hasNext: boolean) => void;
  toggleDownloadModal: () => void;
  toggleAssetDetail: () => void;
  toggleManageFavoriteModal: () => void;
  setPageNumberProp?: any;
}

export default function CatalogCards(props: CatalogCardsProps) {
  const {
    assetLength,
    loading,
    restApiAssets,
    pageNumberProp = null,
    hasNext,
    setHasNext,
    toggleDownloadModal,
    toggleAssetDetail,
    toggleManageFavoriteModal,
    setPageNumberProp = null,
  } = props;

  const dispatch = useAppDispatch();
  const location = useLocation();
  const totalPages = useAppSelector((state) => state.sort.totalPages);
  const pageNumber = pageNumberProp
    ? pageNumberProp
    : useAppSelector((state) => state.sort.pageNumber);
  const Favorites = useAppSelector(
    (state: RootState) => state.newFavorite.favorite
  );

  const setPageNumberAction = (value: number) =>
    setPageNumberProp
      ? setPageNumberProp(value)
      : dispatch(setPageNumber(value));

  const initialSelection: string[] = [];
  const [highlightedAsset, setHighlightedAsset] = useState<AssetInfo>();
  const [videoThumbnails, setVideoThumbnails] = useState<VideoThumbnail>({});
  const [documentThumbnails, setDocumentThumbnails] =
    useState<DocumentThumbnail>({});

  const {
    setAssetData,
    handleCardClick,
    handleTouchEnd,
    selectedList,
    clearMultiSelections,
    isMobileSelectionMode,
    toggleMobileSelectionMode,
  } = useMultiSelection("getAssetListing", "image", initialSelection);

  const allFavoriteIds: string[] = [];
  Object.values(Favorites).forEach((folder) => {
    folder.assets.forEach((asset) => {
      allFavoriteIds.push(String(asset.id));
    });
  });

  const addPageNumberForSearchAndViewAll = useCallback(
    (node: any) => {
      if (!loading && assetLength) {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
          if (
            entries[0].isIntersecting &&
            checkIfHasNextSearchAndViewAll(assetLength, pageNumber + 1)
          ) {
            setPageNumberAction(pageNumber + 1);
          }
        });
        if (node) observer.current.observe(node);
      }
    },
    [loading, assetLength, pageNumber, setPageNumberAction]
  );

  const addPageNumberForCatalogs = useCallback(
    (node: any) => {
      if (!loading && totalPages) {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
          if (
            entries[0].isIntersecting &&
            checkIfHasNextCatalogs(pageNumber + 1, totalPages)
          ) {
            setPageNumberAction(pageNumber + 1);
          }
        });
        if (node) observer.current.observe(node);
      }
    },
    [loading, totalPages, pageNumber, setPageNumberAction]
  );

  const observer = useRef<IntersectionObserver | null>(null);
  const lastCardElementRef = useCallback(
    (node: any) => {
      if (location.pathname === "/search" || location.pathname === "/viewall") {
        addPageNumberForSearchAndViewAll(node);
      } else {
        addPageNumberForCatalogs(node);
      }
    },
    [loading, hasNext, assetLength, totalPages]
  );

  useEffect(() => {
    if (selectedList[0] && restApiAssets) {
      const asset = restApiAssets.find(
        (assets: AssetInfo) => assets.id === selectedList[0]
      );
      setHighlightedAsset(asset);
    }

    dispatch(selectAsset(selectedList));
  }, [selectedList]);

  useEffect(() => {
    let navInitialState = {
      ...initialState,
      enableDownload: false,
      enableShare: false,
      enableFavorite: false,
    };
    if (selectedList.length > 0) {
      navInitialState = {
        ...initialState,
        enableDownload: true,
        enableShare: true,
        enableFavorite: true,
      };
    }
    dispatch(setInPageNavState(navInitialState));
  }, [selectedList]);

  useEffect(() => {
    if (highlightedAsset) {
      const filename: string = highlightedAsset.filename.toString();
      const ext =
        filename.substring(filename.lastIndexOf(".") + 1, filename.length) ||
        filename;
      const Url = // eslint-disable-next-line no-nested-ternary
        highlightedAsset.type !== "video"
          ? highlightedAsset.type === "document" && ext.toUpperCase() === "PDF"
            ? documentThumbnails[highlightedAsset.id]
            : getAssetThumbnailUrl(highlightedAsset)
          : highlightedAsset.youTubeSDThumbnailPath;
      dispatch(assetIdNav(highlightedAsset.id as string));
      dispatch(assetUrlNav(Url));
      dispatch(assetTypeNav(highlightedAsset.type as string));
    }
  }, [highlightedAsset]);

  useEffect(() => {
    if (restApiAssets) {
      setAssetData(restApiAssets);
      dispatch(setAssets(restApiAssets));
      const videoAssetIds = restApiAssets
        .filter((searchListItem) => searchListItem.type.toString() === "video")
        .map((item) => item.id);
      getVideoThumbnails(videoAssetIds.toString(), setVideoThumbnails);
      const documentAssetIds = restApiAssets
        .filter(
          (searchListItem) => searchListItem.type.toString() === "document"
        )
        .map((item) => item.id);
      getDocumentThumbnails(documentAssetIds.toString(), setDocumentThumbnails);
    }
  }, [restApiAssets]);

  useEffect(() => {
    if (location.pathname === "/search" || location.pathname === "/viewall") {
      if (assetLength) {
        setHasNext(checkIfHasNextSearchAndViewAll(assetLength, pageNumber + 1));
      }
    } else {
      setHasNext(checkIfHasNextCatalogs(pageNumber + 1, totalPages));
    }
  }, [assetLength, pageNumber]);

  const isCatalogPage = useMemo(() => {
    return location.pathname !== "/search" && location.pathname !== "/viewall";
  }, [location.pathname]);

  return (
    <>
      {restApiAssets &&
        restApiAssets.map((asset: AssetInfo, assetIndex: number) => {
          const additionalClasses = selectedList.includes(asset.id)
            ? "selected"
            : "";
          const filename: string = asset.filename.toString();
          let fullPath = asset.fullpath;
          if (asset.fullpath.slice(0, 5) !== "https") {
            fullPath = `https://gildan.blob.core.windows.net/gildan/assets${asset.fullpath}${asset.filename}`;
          }
          const ext =
            filename.substring(
              filename.lastIndexOf(".") + 1,
              filename.length
            ) || filename;
          if (restApiAssets.length === assetIndex + 1) {
            return (
              <div
                className="col-xl-3 col-lg-6 col-md-4 col-sm-6 col-12"
                key={`${asset.id}-${asset.filename}`}
                ref={lastCardElementRef}
              >
                <CatalogCard
                  id={asset.id}
                  title={asset.filename}
                  downloadLink={fullPath}
                  shareLink={fullPath}
                  src={
                    // eslint-disable-next-line no-nested-ternary
                    asset.type !== "video"
                      ? asset.type === "document" && ext.toUpperCase() === "PDF"
                        ? documentThumbnails[asset.id]
                        : getAssetThumbnailUrl(asset)
                      : asset.youTubeSDThumbnailPath
                  }
                  type={asset.type}
                  filename={asset.filename}
                  fullpath={fullPath}
                  fullpathExtandFont={asset.fullpathExtandFont}
                  fullpathExtandLandscapeImage={
                    asset.fullpathExtandLandscapeImage
                  }
                  fullpathExtandPortraitImage={
                    asset.fullpathExtandPortraitImage
                  }
                  fullpathThumbnailFont={asset.fullpathThumbnailFont}
                  fullpathThumbnailImageDownload={
                    asset.fullpathThumbnailImageDownload
                  }
                  fullpathThumbnailLandscapeImage={
                    asset.fullpathThumbnailLandscapeImage
                  }
                  fullpathThumbnailPortraitImage={
                    asset.fullpathThumbnailPortraitImage
                  }
                  youTubeSDThumbnailPath={asset.youTubeSDThumbnailPath}
                  youTubeSDPath={asset.youTubeSDPath}
                  customMetadataId={asset.customMetadataId}
                  creationDate={asset.creationDate}
                  fileSize={asset.fileSize}
                  additionalClasses={additionalClasses}
                  selectCallback={handleCardClick}
                  touchEndCallback={handleTouchEnd}
                  toggleAssetDownloadModal={toggleDownloadModal}
                  toggleAssetDetailModal={toggleAssetDetail}
                  toggleAssetFavoriteModal={toggleManageFavoriteModal}
                  isFavorite={allFavoriteIds.includes(String(asset.id))}
                  isNew={asset.assetTag === 1}
                  dpiAsset={asset.dpiAsset}
                />
              </div>
            );
          }
          return (
            <div
              className="col-xl-3 col-lg-6 col-md-4 col-sm-6 col-12"
              key={`${asset.id}-${asset.filename}`}
            >
              <CatalogCard
                id={asset.id}
                title={asset.filename}
                downloadLink={fullPath}
                shareLink={fullPath}
                src={
                  // eslint-disable-next-line no-nested-ternary
                  asset.type !== "video"
                    ? asset.type === "document" && ext.toUpperCase() === "PDF"
                      ? documentThumbnails[asset.id]
                      : getAssetThumbnailUrl(asset)
                    : asset.youTubeSDThumbnailPath
                }
                type={asset.type}
                filename={asset.filename}
                fullpath={fullPath}
                fullpathExtandFont={asset.fullpathExtandFont}
                fullpathExtandLandscapeImage={
                  asset.fullpathExtandLandscapeImage
                }
                fullpathExtandPortraitImage={asset.fullpathExtandPortraitImage}
                fullpathThumbnailFont={asset.fullpathThumbnailFont}
                fullpathThumbnailImageDownload={
                  asset.fullpathThumbnailImageDownload
                }
                fullpathThumbnailLandscapeImage={
                  asset.fullpathThumbnailLandscapeImage
                }
                fullpathThumbnailPortraitImage={
                  asset.fullpathThumbnailPortraitImage
                }
                youTubeSDThumbnailPath={asset.youTubeSDThumbnailPath}
                youTubeSDPath={asset.youTubeSDPath}
                customMetadataId={asset.customMetadataId}
                creationDate={asset.creationDate}
                fileSize={asset.fileSize}
                additionalClasses={additionalClasses}
                selectCallback={handleCardClick}
                touchEndCallback={handleTouchEnd}
                toggleAssetDownloadModal={toggleDownloadModal}
                toggleAssetDetailModal={toggleAssetDetail}
                toggleAssetFavoriteModal={toggleManageFavoriteModal}
                isFavorite={allFavoriteIds.includes(String(asset.id))}
                isNew={asset.assetTag === 1}
                dpiAsset={asset.dpiAsset}
              />
            </div>
          );
        })}
      {((loading && pageNumber > 1) || (isCatalogPage && hasNext)) && (
        <SmallLoader />
      )}

      <MobileSelectionControls
        isMobileSelectionMode={isMobileSelectionMode}
        onToggleSelectionMode={toggleMobileSelectionMode}
      />
    </>
  );
}
